import React, { useState, useEffect } from 'react';
import { Box, Grid, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  HourglassEmpty as HourglassIcon,
  PlayArrow as PlayArrowIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { adminAPI } from '../../../utils/api';

const QueueMonitorTab = ({ metrics }) => {
  const [failedJobs, setFailedJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogContent, setDialogContent] = useState({ title: '', message: '' });

  useEffect(() => {
    fetchFailedJobs();
  }, []);

  const fetchFailedJobs = async () => {
    try {
      const res = await adminAPI.getFailedJobs(10);
      setFailedJobs(res.data.jobs);
    } catch (error) {
      console.error('Failed to fetch failed jobs:', error);
    }
  };

  const handleClearFailed = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.clearFailedJobs();
      setDialogContent({ title: 'Success!', message: res.data.message });
      setDialogOpen(true);
      await fetchFailedJobs();
      window.location.reload(); // Refresh metrics
    } catch (error) {
      setDialogContent({ title: 'Error', message: 'Failed to clear failed jobs' });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleClearCompleted = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.clearCompletedJobs();
      setDialogContent({ title: 'Success!', message: res.data.message });
      setDialogOpen(true);
      window.location.reload(); // Refresh metrics
    } catch (error) {
      setDialogContent({ title: 'Error', message: 'Failed to clear completed jobs' });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleRetryFailed = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.retryFailedJobs();
      setDialogContent({ title: 'Success!', message: res.data.message });
      setDialogOpen(true);
      await fetchFailedJobs();
      window.location.reload(); // Refresh metrics
    } catch (error) {
      setDialogContent({ title: 'Error', message: 'Failed to retry failed jobs' });
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const queue = metrics.queues.managerIntelSync;

  const totalJobs = queue.waiting + queue.active + queue.failed + queue.completed + queue.delayed;
  const activePercent = totalJobs > 0 ? (queue.active / totalJobs) * 100 : 0;
  const completedPercent = totalJobs > 0 ? (queue.completed / totalJobs) * 100 : 0;
  const failedPercent = totalJobs > 0 ? (queue.failed / totalJobs) * 100 : 0;

  const statusCards = [
    {
      label: 'Waiting',
      value: queue.waiting,
      color: '#F59E0B',
      icon: <HourglassIcon />,
      description: 'Jobs queued for processing',
    },
    {
      label: 'Active',
      value: queue.active,
      color: '#3B82F6',
      icon: <PlayArrowIcon />,
      description: 'Currently processing',
    },
    {
      label: 'Delayed',
      value: queue.delayed,
      color: '#8B5CF6',
      icon: <HourglassIcon />,
      description: 'Scheduled for later',
    },
    {
      label: 'Completed',
      value: queue.completed,
      color: '#10B981',
      icon: <CheckCircleIcon />,
      description: 'Successfully finished',
    },
    {
      label: 'Failed',
      value: queue.failed,
      color: '#EF4444',
      icon: <ErrorIcon />,
      description: 'Errors encountered',
    },
  ];

  return (
    <Box>
      {/* Queue Controls */}
      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#3B82F6' }}>
          ⚙️ Queue Controls
        </Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={handleRetryFailed}
              disabled={loading || queue.failed === 0}
              sx={{
                background: '#3B82F6',
                color: '#FFF',
                fontWeight: 700,
                padding: '12px',
                '&:hover': {
                  background: '#2563EB',
                },
              }}
            >
              Retry Failed ({queue.failed})
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<DeleteIcon />}
              onClick={handleClearFailed}
              disabled={loading || queue.failed === 0}
              sx={{
                background: '#EF4444',
                color: '#FFF',
                fontWeight: 700,
                padding: '12px',
                '&:hover': {
                  background: '#DC2626',
                },
              }}
            >
              Clear Failed ({queue.failed})
            </Button>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<DeleteSweepIcon />}
              onClick={handleClearCompleted}
              disabled={loading}
              sx={{
                background: '#6B7280',
                color: '#FFF',
                fontWeight: 700,
                padding: '12px',
                '&:hover': {
                  background: '#4B5563',
                },
              }}
            >
              Clear Completed
            </Button>
          </Grid>
        </Grid>
      </Box>

      {/* Health Banner */}
      <Box
        sx={{
          background: queue.failed > 5 ? '#7F1D1D' : queue.waiting > 20 ? '#78350F' : '#064E3B',
          border: `2px solid ${queue.failed > 5 ? '#EF4444' : queue.waiting > 20 ? '#F59E0B' : '#10B981'}`,
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
          <Box>
            <Box sx={{ fontSize: '20px', fontWeight: 700, marginBottom: '4px' }}>
              Queue Health: {queue.failed > 5 ? '🔴 CRITICAL' : queue.waiting > 20 ? '🟡 WARNING' : '🟢 HEALTHY'}
            </Box>
            <Box sx={{ fontSize: '14px', color: '#D1D5DB' }}>
              {totalJobs} total jobs · {queue.active} processing · {queue.waiting} waiting
            </Box>
          </Box>
          <Box sx={{ fontSize: '48px' }}>
            {queue.failed > 5 ? '🚨' : queue.waiting > 20 ? '⚠️' : '✅'}
          </Box>
        </Box>

        {/* Progress Bar */}
        <Box sx={{ marginBottom: '8px' }}>
          <Box sx={{ display: 'flex', height: '32px', borderRadius: '8px', overflow: 'hidden' }}>
            {activePercent > 0 && (
              <Box
                sx={{
                  width: `${activePercent}%`,
                  background: '#3B82F6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {activePercent > 5 && `${activePercent.toFixed(0)}%`}
              </Box>
            )}
            {completedPercent > 0 && (
              <Box
                sx={{
                  width: `${completedPercent}%`,
                  background: '#10B981',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {completedPercent > 5 && `${completedPercent.toFixed(0)}%`}
              </Box>
            )}
            {failedPercent > 0 && (
              <Box
                sx={{
                  width: `${failedPercent}%`,
                  background: '#EF4444',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '11px',
                  fontWeight: 700,
                }}
              >
                {failedPercent > 5 && `${failedPercent.toFixed(0)}%`}
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ fontSize: '11px', color: '#D1D5DB', display: 'flex', gap: '16px' }}>
          <span>🔵 Active: {queue.active}</span>
          <span>🟢 Completed: {queue.completed}</span>
          <span>🔴 Failed: {queue.failed}</span>
        </Box>
      </Box>

      {/* Status Cards */}
      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        {statusCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={4} lg={2.4} key={index}>
            <Box
              sx={{
                background: '#1E293B',
                border: card.value > 0 ? `2px solid ${card.color}` : '1px solid #334155',
                borderRadius: '12px',
                padding: '20px',
                textAlign: 'center',
                transition: 'all 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: `0 8px 24px ${card.color}44`,
                },
              }}
            >
              <Box
                sx={{
                  fontSize: '40px',
                  color: card.color,
                  marginBottom: '12px',
                }}
              >
                {React.cloneElement(card.icon, { sx: { fontSize: '40px' } })}
              </Box>
              <Box sx={{ fontSize: '36px', fontWeight: 700, color: card.color, marginBottom: '8px' }}>
                {card.value}
              </Box>
              <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                {card.label}
              </Box>
              <Box sx={{ fontSize: '11px', color: '#94A3B8' }}>
                {card.description}
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Recent Profiles Being Synced */}
      <Box
        sx={{
          background: '#1E293B',
          border: '1px solid #334155',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '24px',
        }}
      >
        <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#3B82F6' }}>
          🧠 Recent Manager Intel Profiles
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '400px', overflow: 'auto' }}>
          {metrics.managerIntelProfiles.slice(0, 15).map((profile, index) => (
            <Box
              key={index}
              component="a"
              href={`/manager-intel/${profile.sleeperUsername}`}
              target="_blank"
              sx={{
                background: '#0F172A',
                border: '1px solid #334155',
                borderRadius: '8px',
                padding: '16px 20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                textDecoration: 'none',
                transition: 'all 0.2s',
                '&:hover': {
                  background: '#1E293B',
                  borderColor: '#3B82F6',
                  transform: 'translateX(4px)',
                },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px', flex: 1 }}>
                <Box
                  sx={{
                    width: '12px',
                    height: '12px',
                    borderRadius: '50%',
                    background:
                      profile.status === 'complete'
                        ? '#10B981'
                        : profile.status === 'error'
                        ? '#EF4444'
                        : profile.status === 'in_progress'
                        ? '#3B82F6'
                        : '#F59E0B',
                    boxShadow:
                      profile.status === 'complete'
                        ? '0 0 8px #10B981'
                        : profile.status === 'error'
                        ? '0 0 8px #EF4444'
                        : '0 0 8px #F59E0B',
                    animation: profile.status === 'in_progress' ? 'pulse 2s infinite' : 'none',
                    '@keyframes pulse': {
                      '0%, 100%': { opacity: 1 },
                      '50%': { opacity: 0.5 },
                    },
                  }}
                />
                <Box>
                  <Box sx={{ fontSize: '15px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                    {profile.sleeperUsername}
                  </Box>
                  <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                    Last synced: {profile.lastRefreshedAt ? new Date(profile.lastRefreshedAt).toLocaleString() : 'Never'}
                  </Box>
                </Box>
              </Box>

              <Chip
                label={profile.status.toUpperCase()}
                size="small"
                sx={{
                  background:
                    profile.status === 'complete'
                      ? '#065F46'
                      : profile.status === 'error'
                      ? '#7F1D1D'
                      : profile.status === 'in_progress'
                      ? '#1E3A8A'
                      : '#78350F',
                  color: '#FFF',
                  fontWeight: 700,
                  fontSize: '11px',
                }}
              />
            </Box>
          ))}
        </Box>
      </Box>

      {/* Queue Performance Metrics */}
      <Grid container spacing={3} sx={{ marginBottom: '24px' }}>
        <Grid item xs={12} md={4}>
          <MetricBox
            label="Success Rate"
            value={`${totalJobs > 0 ? ((queue.completed / totalJobs) * 100).toFixed(1) : 0}%`}
            color="#10B981"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricBox
            label="Failure Rate"
            value={`${totalJobs > 0 ? ((queue.failed / totalJobs) * 100).toFixed(1) : 0}%`}
            color="#EF4444"
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <MetricBox
            label="Queue Depth"
            value={queue.waiting + queue.delayed}
            color="#F59E0B"
          />
        </Grid>
      </Grid>

      {/* Failed Jobs Detail */}
      {failedJobs.length > 0 && (
        <Box
          sx={{
            background: '#1E293B',
            border: '2px solid #EF4444',
            borderRadius: '12px',
            padding: '24px',
          }}
        >
          <Box sx={{ fontSize: '18px', fontWeight: 700, marginBottom: '20px', color: '#EF4444' }}>
            🚨 Failed Jobs Details (Last 10)
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {failedJobs.map((job, index) => (
              <Box
                key={job.id}
                sx={{
                  background: '#0F172A',
                  border: '1px solid #7F1D1D',
                  borderRadius: '8px',
                  padding: '16px 20px',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                  <Box>
                    <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#F1F5F9', marginBottom: '4px' }}>
                      Job #{job.id}
                    </Box>
                    <Box sx={{ fontSize: '12px', color: '#94A3B8' }}>
                      Username: {job.data?.sleeperUsername || 'N/A'} · Attempts: {job.attemptsMade}
                    </Box>
                  </Box>
                  <Chip
                    label="FAILED"
                    size="small"
                    sx={{
                      background: '#7F1D1D',
                      color: '#FEF2F2',
                      fontWeight: 700,
                      fontSize: '10px',
                    }}
                  />
                </Box>
                <Box
                  sx={{
                    background: '#000',
                    border: '1px solid #334155',
                    borderRadius: '6px',
                    padding: '12px',
                    fontFamily: 'monospace',
                    fontSize: '11px',
                    color: '#FCA5A5',
                    maxHeight: '100px',
                    overflow: 'auto',
                  }}
                >
                  {job.failedReason || 'No error message'}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        PaperProps={{
          sx: {
            background: '#1E293B',
            border: '1px solid #334155',
            color: '#F1F5F9',
          },
        }}
      >
        <DialogTitle sx={{ color: '#F1F5F9', fontWeight: 700 }}>
          {dialogContent.title}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ color: '#94A3B8' }}>{dialogContent.message}</Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDialogOpen(false)}
            sx={{
              color: '#3B82F6',
              fontWeight: 700,
            }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const MetricBox = ({ label, value, color }) => (
  <Box
    sx={{
      background: '#1E293B',
      border: `2px solid ${color}`,
      borderRadius: '12px',
      padding: '24px',
      textAlign: 'center',
    }}
  >
    <Box sx={{ fontSize: '48px', fontWeight: 700, color, marginBottom: '8px' }}>
      {value}
    </Box>
    <Box sx={{ fontSize: '14px', fontWeight: 600, color: '#94A3B8', textTransform: 'uppercase' }}>
      {label}
    </Box>
  </Box>
);

export default QueueMonitorTab;
